import type { BreadcrumbItem } from '@/components/ui/Breadcrumb';

const UUID_OR_INT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Build breadcrumb items from the current pathname.
 *
 * Path mechanics (locale-prefix + segment split + href building) live here so
 * every dashboard renders breadcrumbs the same way. Label resolution is
 * delegated to the caller, since each dashboard translates segment names from
 * a different i18n namespace (admin vs dashboard).
 *
 * hrefs always include the `/${locale}` prefix required by the router, and the
 * last segment is rendered as the current page (no href).
 */
export function buildBreadcrumbs(
  pathname: string,
  resolveLabel: (segment: string, index: number) => string,
): BreadcrumbItem[] {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return [];

  const locale = parts[0];
  const segments = parts.slice(1);
  // Only the root segment (e.g. "dashboard") → nothing to breadcrumb.
  if (segments.length <= 1) return [];

  return segments.map((seg, i) => ({
    label: resolveLabel(seg, i),
    href: i < segments.length - 1 ? `/${locale}/${segments.slice(0, i + 1).join('/')}` : undefined,
  }));
}

/** True for UUIDs or pure integers (IDs) — rendered verbatim, not translated. */
export function isIdSegment(segment: string): boolean {
  return UUID_OR_INT.test(segment) || /^\d+$/.test(segment);
}
